import React from "react";
import { motion } from "framer-motion";
import { FaWhatsapp, FaCrown } from "react-icons/fa";
import { IoGameController } from "react-icons/io5";
import { LuTarget, LuChevronRight, LuLayoutGrid } from "react-icons/lu";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";

const PLATFORMS = [
  { code: "DG", href: "https://www.dgjogo6.com/?ch=740003&ic=601072209#/register" },
  { code: "YG", href: "https://ygjogo6.com/?ch=550003&ic=601077266#/register" },
  { code: "MG", href: "https://mgjogo6.com/?ch=350004&ic=140484180#/register" },
  { code: "HG", href: "https://hgjogo666.com/?ch=110003&ic=140493088#/register" },
  { code: "EG", href: "https://egjogo6.com/?ch=380002&ic=140437835#/register" },
  { code: "FG", href: "https://fgjogo6.com/?ch=230002&ic=140493455#/register" },
  { code: "WG", href: "http://wgpixel.wgjogo6.com/?referralCode=rcx5176" },
  { code: "BG", href: "https://www.bgjogo6.com/register?r=pga0908" },
  { code: "AG", href: "http://agpixel.agjogo6.com/?referralCode=ikh1032" },
];

const containerVariants = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: { staggerChildren: 0.09, delayChildren: 0.1 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 22 },
  show: {
    opacity: 1,
    y: 0,
    transition: { type: "spring", stiffness: 280, damping: 26 },
  },
};

const SecondaryLink = ({
  href,
  testId,
  icon: Icon,
  label,
  accentClass,
  iconBg,
}) => (
  <motion.a
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    data-testid={testId}
    variants={itemVariants}
    whileHover={{ scale: 1.02 }}
    whileTap={{ scale: 0.98 }}
    className={`group relative flex w-full items-center rounded-2xl border ${accentClass} backdrop-blur-xl py-4 px-4 transition-colors`}
  >
    <span
      className={`flex h-10 w-10 items-center justify-center rounded-xl ${iconBg} shrink-0`}
    >
      <Icon className="text-xl" />
    </span>
    <span className="flex-1 text-center font-semibold tracking-wide text-[15px] sm:text-base pr-10">
      {label}
    </span>
  </motion.a>
);

export default function LinkTree() {
  return (
    <div className="relative min-h-screen w-full overflow-hidden text-white">
      {/* Animated mesh background */}
      <div className="bg-mesh" aria-hidden />
      <div className="bg-grid" aria-hidden />
      <div className="orb orb-1" aria-hidden />
      <div className="orb orb-2" aria-hidden />

      {/* Diagonal light beams */}
      <div className="bg-beam b1" aria-hidden />
      <div className="bg-beam b2" aria-hidden />
      <div className="bg-beam b3" aria-hidden />

      {/* Twinkling stars */}
      <span className="bg-star t1" aria-hidden />
      <span className="bg-star t2" aria-hidden />
      <span className="bg-star t3" aria-hidden />
      <span className="bg-star t4" aria-hidden />
      <span className="bg-star t5" aria-hidden />
      <span className="bg-star t6" aria-hidden />
      <span className="bg-star t7" aria-hidden />
      <span className="bg-star t8" aria-hidden />
      <span className="bg-star t9" aria-hidden />
      <span className="bg-star t10" aria-hidden />

      {/* Rising gold particles */}
      <span className="bg-particle p1" aria-hidden />
      <span className="bg-particle p2" aria-hidden />
      <span className="bg-particle p3" aria-hidden />
      <span className="bg-particle p4" aria-hidden />
      <span className="bg-particle p5" aria-hidden />
      <span className="bg-particle p6" aria-hidden />
      <span className="bg-particle p7" aria-hidden />
      <span className="bg-particle p8" aria-hidden />
      <span className="bg-particle p9" aria-hidden />
      <span className="bg-particle p10" aria-hidden />
      <span className="bg-particle p11" aria-hidden />
      <span className="bg-particle p12" aria-hidden />
      {/* Vignette for legibility */}
      <div
        className="fixed inset-0 z-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(ellipse at center, transparent 0%, rgba(0,0,0,0.55) 75%, rgba(0,0,0,0.85) 100%)",
        }}
        aria-hidden
      />
      {/* Grain */}
      <div className="grain" aria-hidden />

      {/* Content */}
      <div className="relative z-10 mx-auto flex min-h-screen max-w-md flex-col px-5 py-10 sm:py-14">
        {/* Hero — Welcome logo */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ type: "spring", stiffness: 200, damping: 22 }}
          className="relative flex flex-col items-center text-center py-8"
          data-testid="hero-welcome"
        >
          {/* Decorative background layers */}
          <div className="hero-rays" aria-hidden />
          <div className="hero-aura" aria-hidden />

          {/* Floating sparkles */}
          <span className="sparkle s1" aria-hidden />
          <span className="sparkle s2" aria-hidden />
          <span className="sparkle s3" aria-hidden />
          <span className="sparkle s4" aria-hidden />
          <span className="sparkle s5" aria-hidden />
          <span className="sparkle s6" aria-hidden />

          {/* Crown */}
          <motion.div
            initial={{ opacity: 0, y: -10, scale: 0.5 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ delay: 0.1, type: "spring", stiffness: 220, damping: 14 }}
            className="crown-bounce mb-2"
          >
            <FaCrown className="text-3xl text-[#FFD700]" />
          </motion.div>

          {/* Hero with side ornaments */}
          <div className="flex items-center gap-3 w-full">
            <motion.div
              className="hero-ornament"
              initial={{ scaleX: 0, opacity: 0 }}
              animate={{ scaleX: 1, opacity: 1 }}
              transition={{ delay: 0.5, duration: 0.7 }}
              style={{ transformOrigin: "right" }}
              aria-hidden
            />

            {/* Title — letter by letter */}
            <h1
              className="hero-title text-[2.25rem] sm:text-5xl leading-none whitespace-nowrap"
              data-testid="hero-title"
              aria-label="Bem-vindos"
            >
              {"BEM-VINDOS".split("").map((char, i) => (
                <motion.span
                  key={i}
                  initial={{ opacity: 0, y: 30, rotateX: -90 }}
                  animate={{ opacity: 1, y: 0, rotateX: 0 }}
                  transition={{
                    delay: 0.3 + i * 0.07,
                    type: "spring",
                    stiffness: 260,
                    damping: 18,
                  }}
                  style={{ display: "inline-block" }}
                >
                  {char}
                </motion.span>
              ))}
            </h1>

            <motion.div
              className="hero-ornament"
              initial={{ scaleX: 0, opacity: 0 }}
              animate={{ scaleX: 1, opacity: 1 }}
              transition={{ delay: 0.5, duration: 0.7 }}
              style={{ transformOrigin: "left" }}
              aria-hidden
            />
          </div>

          {/* Subtitle ornament — small diamond divider */}
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.4, duration: 0.6 }}
            className="mt-4 flex items-center gap-3"
          >
            <span className="block h-px w-8 bg-gradient-to-r from-transparent to-[#D4AF37]" />
            <span className="text-[#D4AF37] text-xs">◆</span>
            <span className="block h-px w-8 bg-gradient-to-l from-transparent to-[#D4AF37]" />
          </motion.div>
        </motion.div>

        {/* Buttons stack */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="show"
          className="mt-10 flex flex-col gap-4"
        >
          {/* === MAIN HIGHLIGHT #1: Plataforma XG (NOVA — BLACK & GOLD) === */}
          <motion.div
            variants={itemVariants}
            className="relative"
          >
            {/* Badge "NOVA" — fora do overflow-hidden do link */}
            <span
              className="absolute -top-2 right-3 z-20 rounded-full px-2.5 py-[3px] text-[10px] font-extrabold tracking-[0.22em] pointer-events-none"
              style={{
                background: "linear-gradient(135deg, #FFE17A 0%, #D4AF37 50%, #8B6914 100%)",
                color: "#0a0a0a",
                boxShadow: "0 0 14px rgba(212, 175, 55, 0.7), 0 2px 6px rgba(0,0,0,0.5)",
                border: "1px solid rgba(255, 225, 122, 0.6)",
              }}
            >
              NOVA
            </span>
            <motion.a
              href="https://xgjogo2.com/register/?ch=190000"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="link-platform-xg"
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
              className="vg-pulse shine relative flex w-full items-center justify-center overflow-hidden rounded-2xl px-5 py-6"
              style={{
                background:
                  "linear-gradient(135deg, #0a0a0a 0%, #1a1410 50%, #0a0a0a 100%)",
                border: "1px solid rgba(212, 175, 55, 0.6)",
                color: "#FFE17A",
              }}
            >
              <div
                className="absolute left-5 flex h-12 w-12 items-center justify-center rounded-xl"
                style={{
                  background: "linear-gradient(135deg, #D4AF37 0%, #8B6914 100%)",
                  boxShadow: "0 0 18px rgba(212, 175, 55, 0.4)",
                }}
              >
                <FaCrown className="text-2xl text-black" />
              </div>
              <div className="flex flex-col items-center px-12">
                <span
                  className="font-black text-2xl leading-tight tracking-tight"
                  style={{
                    background:
                      "linear-gradient(180deg, #FFF6D5 0%, #FFE17A 40%, #D4AF37 100%)",
                    WebkitBackgroundClip: "text",
                    backgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                  }}
                >
                  PLATAFORMA XG
                </span>
                <span className="text-[11px] font-semibold tracking-[0.2em] uppercase text-[#D4AF37]/70">
                  Cadastro oficial
                </span>
              </div>
            </motion.a>
          </motion.div>

          {/* === MAIN HIGHLIGHT #2: Plataforma VG (BLACK & GOLD) === */}
          <motion.a
            href="https://vgjogo7.com/?ch=160000&ic=1251328"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="link-platform-vg"
            variants={itemVariants}
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.97 }}
            className="shine relative flex w-full items-center justify-center overflow-hidden rounded-2xl px-5 py-6"
            style={{
              background:
                "linear-gradient(135deg, #0a0a0a 0%, #1a1410 50%, #0a0a0a 100%)",
              border: "1px solid rgba(212, 175, 55, 0.5)",
              color: "#FFE17A",
            }}
          >
            <div
              className="absolute left-5 flex h-12 w-12 items-center justify-center rounded-xl"
              style={{
                background: "linear-gradient(135deg, #D4AF37 0%, #8B6914 100%)",
                boxShadow: "0 0 18px rgba(212, 175, 55, 0.4)",
              }}
            >
              <IoGameController className="text-3xl text-black" />
            </div>
            <div className="flex flex-col items-center px-12">
              <span
                className="font-black text-2xl leading-tight tracking-tight"
                style={{
                  background:
                    "linear-gradient(180deg, #FFF6D5 0%, #FFE17A 40%, #D4AF37 100%)",
                  WebkitBackgroundClip: "text",
                  backgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                PLATAFORMA VG
              </span>
              <span className="text-[11px] font-semibold tracking-[0.2em] uppercase text-[#D4AF37]/70">
                Link oficial
              </span>
            </div>
          </motion.a>

          {/* === Outras Plataformas (Modal) — abre lista interna === */}
          <Dialog>
            <DialogTrigger asChild>
              <motion.button
                type="button"
                data-testid="open-platforms-modal"
                variants={itemVariants}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="group relative flex w-full items-center rounded-2xl border border-[#D4AF37]/15 bg-white/[0.03] backdrop-blur-xl py-4 px-4 transition-colors hover:border-[#D4AF37]/40 hover:bg-white/[0.06]"
              >
                <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#D4AF37]/10 border border-[#D4AF37]/20 shrink-0">
                  <LuLayoutGrid className="text-xl text-[#D4AF37]" />
                </span>
                <span className="flex-1 text-center font-semibold tracking-wide text-[15px] sm:text-base pr-10 text-white/90">
                  Nossas Plataformas
                </span>
              </motion.button>
            </DialogTrigger>

            <DialogContent
              data-testid="platforms-modal"
              className="max-w-md max-h-[85vh] overflow-y-auto rounded-2xl border-[#D4AF37]/25 bg-gradient-to-br from-[#0a0a0a] via-[#120e07] to-[#0a0a0a] p-6 text-white shadow-[0_20px_60px_-15px_rgba(212,175,55,0.4)]"
            >
              <DialogHeader>
                <DialogTitle
                  className="text-center text-2xl font-bold tracking-wide"
                  style={{
                    fontFamily: "Cinzel, serif",
                    background:
                      "linear-gradient(180deg, #FFF6D5 0%, #FFE17A 50%, #D4AF37 100%)",
                    WebkitBackgroundClip: "text",
                    backgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                  }}
                >
                  Plataformas
                </DialogTitle>
                <p className="text-center text-xs text-white/50 mt-1">
                  Escolha uma plataforma para acessar
                </p>
              </DialogHeader>

              {/* Decorative divider */}
              <div className="my-2 flex items-center gap-3">
                <span className="block h-px flex-1 bg-gradient-to-r from-transparent to-[#D4AF37]/50" />
                <span className="text-[#D4AF37] text-xs">◆</span>
                <span className="block h-px flex-1 bg-gradient-to-l from-transparent to-[#D4AF37]/50" />
              </div>

              {/* Platforms grid */}
              <div className="grid grid-cols-1 gap-2.5 mt-2">
                {PLATFORMS.map((p) => (
                  <motion.a
                    key={p.code}
                    href={p.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`platform-${p.code.toLowerCase()}`}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className="group relative flex items-center gap-3 rounded-xl border border-[#D4AF37]/20 bg-white/[0.03] py-3 px-4 transition-colors hover:border-[#D4AF37]/55 hover:bg-[#D4AF37]/[0.06]"
                  >
                    <span
                      className="flex h-10 w-10 items-center justify-center rounded-lg font-black text-sm shrink-0"
                      style={{
                        background:
                          "linear-gradient(135deg, #D4AF37 0%, #8B6914 100%)",
                        color: "#0a0a0a",
                        boxShadow: "0 0 14px rgba(212, 175, 55, 0.35)",
                      }}
                    >
                      {p.code}
                    </span>
                    <span className="flex-1 font-semibold tracking-wide text-[15px] text-white/90">
                      Plataforma {p.code}
                    </span>
                    <LuChevronRight className="text-[#D4AF37] text-lg opacity-70 group-hover:opacity-100 group-hover:translate-x-1 transition-all" />
                  </motion.a>
                ))}
              </div>
            </DialogContent>
          </Dialog>

          {/* === WhatsApp Direct Contact (Parceria) === */}
          <SecondaryLink
            href="https://wa.me/5511915004878"
            testId="link-whatsapp-contact"
            icon={FaWhatsapp}
            label="Contato para Parceria"
            accentClass="border-[#D4AF37]/20 bg-white/[0.03] text-white/85 hover:border-[#D4AF37]/45 hover:bg-white/[0.06]"
            iconBg="bg-[#25D366] text-black"
          />

          {/* === SITE DE SINAIS (PixelTech) — black & gold === */}
          <motion.div variants={itemVariants} className="w-full pt-2">
            <div
              className="signal-card relative overflow-hidden rounded-2xl border bg-gradient-to-br from-[#0a0a0a] via-[#120e07] to-[#0a0a0a] p-5"
              data-testid="signal-card"
            >
              {/* Decorative gold glow */}
              <div
                className="pointer-events-none absolute -top-16 -right-16 h-44 w-44 rounded-full opacity-40"
                style={{
                  background:
                    "radial-gradient(circle, rgba(212,175,55,0.55) 0%, transparent 70%)",
                  filter: "blur(28px)",
                }}
              />

              {/* Header */}
              <div className="relative flex items-center gap-3">
                <span
                  className="flex h-11 w-11 items-center justify-center rounded-xl"
                  style={{
                    background:
                      "linear-gradient(135deg, #D4AF37 0%, #8B6914 100%)",
                    boxShadow: "0 0 18px rgba(212, 175, 55, 0.4)",
                  }}
                >
                  <LuTarget className="text-xl text-black" />
                </span>
                <div className="flex-1">
                  <h3
                    className="font-extrabold text-lg leading-tight"
                    style={{
                      background:
                        "linear-gradient(180deg, #FFF6D5 0%, #FFE17A 50%, #D4AF37 100%)",
                      WebkitBackgroundClip: "text",
                      backgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                    }}
                  >
                    Site de Sinais
                  </h3>
                  <p className="text-xs text-white/50 mt-0.5 tracking-wide">PixelTech</p>
                </div>
              </div>

              {/* Description */}
              <p className="relative mt-4 text-sm leading-relaxed text-white/70">
                Plataforma com sinais em tempo real dos melhores momentos para jogar.
              </p>

              {/* CTA */}
              <motion.a
                href="https://pixeltech.com.br/"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="link-pixeltech-signals"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="relative mt-5 flex w-full items-center justify-center gap-2 rounded-xl py-3.5 px-4 font-bold transition-all"
                style={{
                  background:
                    "linear-gradient(135deg, #D4AF37 0%, #B8860B 100%)",
                  color: "#0a0a0a",
                  boxShadow: "0 8px 28px -8px rgba(212, 175, 55, 0.55)",
                }}
              >
                <span className="tracking-wide">Acessar Site de Sinais</span>
                <LuChevronRight className="text-lg" />
              </motion.a>
            </div>
          </motion.div>
        </motion.div>

        {/* Footer */}
        <motion.footer
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.2, duration: 0.6 }}
          className="mt-auto pt-12 text-center"
          data-testid="page-footer"
        >
          <p className="text-[10px] uppercase tracking-[0.3em] text-white/30">
            Todos os direitos reservados
          </p>
        </motion.footer>
      </div>
    </div>
  );
}
